/**
 * One cheap call before anything runs: does this key work, and does the model
 * exist? A GET on the model resource; no message content, no tokens spent.
 * Runs on the main thread, straight to Anthropic under the participant's key —
 * the only network request the app makes besides the run itself.
 */
export const ANTHROPIC_API = 'https://api.anthropic.com'
export const ANTHROPIC_VERSION = '2023-06-01'

export type KeyCheck =
  | { readonly ok: true; readonly model: string }
  | {
      readonly ok: false
      readonly reason: 'rejected' | 'model' | 'network' | 'other'
      readonly detail: string
    }

export async function checkKey(
  apiKey: string,
  model: string,
  fetchImpl: typeof fetch = fetch,
): Promise<KeyCheck> {
  let response: Response
  try {
    response = await fetchImpl(`${ANTHROPIC_API}/v1/models/${encodeURIComponent(model)}`, {
      method: 'GET',
      headers: {
        'x-api-key': apiKey,
        'anthropic-version': ANTHROPIC_VERSION,
        'anthropic-dangerous-direct-browser-access': 'true',
      },
    })
  } catch {
    return {
      ok: false,
      reason: 'network',
      detail: 'Could not reach api.anthropic.com. Check the connection and try again.',
    }
  }
  if (response.status === 401 || response.status === 403)
    return { ok: false, reason: 'rejected', detail: 'Anthropic rejected this key.' }
  if (response.status === 404)
    return { ok: false, reason: 'model', detail: `Anthropic has no model named "${model}".` }
  if (!response.ok)
    return {
      ok: false,
      reason: 'other',
      detail: `Anthropic answered with status ${response.status}.`,
    }
  return { ok: true, model }
}
